<template>
  <div class="c-histogram-filter">
    <label class="filter-name pl-2">{{ filter.name }}</label>
    <v-form-group>
      <span :id="`${filterId}-min`" class="sr-only">{{
        `${$t('white-label.components.histogramFilter.minValue')} ${filter.name}`
      }}</span>
      <v-form-number
        class="filter-input min"
        type="float"
        :spin="false"
        :step="0.01"
        :min="filter.extremes.min"
        :max="filter.extremes.max"
        :value="minValue"
        :formatter="minFormatter"
        ref="minInput"
        @change="onInputMin"
        :aria-labelledby="`${filterId}-min`"
      />
    </v-form-group>
    <v-stock-chart class="chart chart-bg" :options="chartOptions" :api="chart" :height="30" theme="dark" />
    <v-form-group>
      <span :id="`${filterId}-max`" class="sr-only">{{
        `${$t('white-label.components.histogramFilter.maxValue')} ${filter.name}`
      }}</span>
      <v-form-number
        class="filter-input max"
        type="float"
        :spin="false"
        :step="0.01"
        :min="filter.extremes.min"
        :max="filter.extremes.max"
        :value="maxValue"
        :formatter="maxFormatter"
        ref="maxInput"
        @change="onInputMax"
        :aria-labelledby="`${filterId}-max`"
      />
    </v-form-group>
    <v-button class="remove-filter p-2" variant="link" @click="$emit('removeFilter', filter.id)">
      <font-awesome-icon :icon="['fas', 'times-circle']" />
      <span class="sr-only">{{ $t('white-label.widgets.stockScreener.buttons.deleteFilter') }}</span>
    </v-button>
  </div>
</template>

<script>
import VStockChart from '@base/VStockChart';

export default {
  name: 'c-histogram-filter',
  components: {
    VStockChart
  },
  props: {
    filter: {
      type: Object,
      required: true
    },
    resolution: {
      type: Number,
      required: true
    }
  },
  watch: {
    userExtremes: {
      handler(newExtremes, oldExtremes) {
        if (newExtremes.min !== oldExtremes.min || newExtremes.max !== oldExtremes.max) {
          // Only on manual input NOT navigator
          if (this.inputUsed === true) {
            this.onSetExtremes();
          }
          this.inputUsed = false;
        }
      }
    },
    extremes: {
      handler(newExtremes, oldExtremes) {
        if (newExtremes.min !== oldExtremes.min && newExtremes.max !== oldExtremes.max) {
          this.calculateHistogramFaktor();

          /* TODO: Check
          this.chart.get(this.seriesHistogram.id).update({
            data: this.filter.data
          });
          this.onSetExtremes();
          */
        }
      },
      immediate: false
    },
    commaSeparator: {
      handler() {
        //in order to avoid store access in the core component, child ref is used to refresh the number formatting
        this.$refs.minInput.setInputValue(this.minValue);
        this.$refs.maxInput.setInputValue(this.maxValue);
      }
    }
  },
  data() {
    return {
      chart: {},
      seriesHistogram: {
        id: 'filterSeries',
        name: 'Histogram',
        colorIndex: 0,
        index: 0,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false
            }
          }
        },
        visible: false
      },
      faktor: null,
      inputUsed: false
    };
  },
  computed: {
    chartOptions() {
      return {
        lang: {
          accessibility: {
            chartContainerLabel: `${this.filter.name} ${this.$t('white-label.globalLabels.chartGraphImage')}`,
            svgContainerLabel: `${this.filter.name} ${this.$t('white-label.globalLabels.chartGraphImage')}`,
            defaultChartTitle: `${this.filter.name} ${this.$t('white-label.globalLabels.chartObject')}`
          }
        },
        accessibility: {
          enabled: false
        },
        chart: {
          height: 45,
          margin: 1
        },
        navigator: {
          height: 30,
          margin: 0,
          xAxis: {
            type: 'logarithmic',
            events: {
              afterSetExtremes: () => {
                this.onSetExtremes();
              }
            },
            visible: false
          },
          yAxis: {
            type: 'logarithmic',
            visible: false
          },
          series: {
            type: 'column',
            pointPadding: 1,
            visible: true
          },
          handles: {
            lineWidth: 0,
            width: 1,
            height: 30
          }
        },
        scrollbar: {
          enabled: false
        },
        rangeSelector: {
          enabled: false
        },
        legend: {
          enabled: false
        },
        tooltip: {
          enabled: false
        },
        xAxis: {
          events: {
            afterSetExtremes: e => {
              if (e.trigger !== undefined && e.trigger === 'navigator') {
                this.onInputNavigator({
                  min: e.min,
                  max: e.max
                });
              }
            }
          },
          labels: {
            enabled: false
          },
          height: 0,
          visible: false
        },
        yAxis: {
          height: 0,
          visible: false,
          labels: {
            enabled: false
          }
        },
        plotOptions: {
          series: {
            turboThreshold: 3000
          }
        },
        series: [this.seriesHistogram]
      };
    },
    userExtremes() {
      return this.filter.userExtremes;
    },
    extremes() {
      return this.filter.extremes;
    },
    minValue() {
      /*
       * NOTE: second condition/check (for undefined) only needed as long as database is not handling JSON correctly
       *       -> Can be removed in later refactoring when this issue is resolved
       */
      const value =
        this.filter.userExtremes.min !== null && this.filter.userExtremes.min !== undefined
          ? this.filter.userExtremes.min
          : this.filter.extremes.min;

      return this.filter.type === 'osc' ? value * 100 : value;
    },
    maxValue() {
      const value =
        this.filter.userExtremes.max !== null && this.filter.userExtremes.max !== undefined
          ? this.filter.userExtremes.max
          : this.filter.extremes.max;

      return this.filter.type === 'osc' ? value * 100 : value;
    },
    decimalSeparator() {
      return this.$n ? this.$n('0.1')[1] : '.';
    },
    commaSeparator() {
      return this.$store.state.applicationSettings.general.settings.numberFormat.commaSeparator;
    },
    filterId() {
      return this.filter.name.replace(/[^A-Z0-9]/gi, '_');
    }
  },
  methods: {
    onInputMin(min) {
      min = this.cleanValue(min);

      if (this.filter.type === 'osc') min = min / 100;
      if (min < this.filter.extremes.min || min > this.filter.extremes.max) min = this.filter.extremes.min;

      const max = this.filter.userExtremes.max !== null ? this.filter.userExtremes.max : this.filter.extremes.max;

      this.$emit('updateHistogramFilter', {
        fid: this.filter.fid,
        id: this.filter.id,
        data: {
          min,
          max
        }
      });
      this.inputUsed = true;
    },
    onInputMax(max) {
      max = this.cleanValue(max);

      if (this.filter.type === 'osc') max = max / 100;
      if (max > this.filter.extremes.max || max < this.filter.extremes.min) max = this.filter.extremes.max;

      const min = this.filter.userExtremes.min !== null ? this.filter.userExtremes.min : this.filter.extremes.min;

      this.$emit('updateHistogramFilter', {
        fid: this.filter.fid,
        id: this.filter.id,
        data: {
          min,
          max
        }
      });
      this.inputUsed = true;
    },
    onInputNavigator(data) {
      // NOTE: Possible min/max range from 0 to (this.resolution - 1)
      const min =
        data.min === 0 ? this.filter.extremes.min : this.filter.extremes.min + parseFloat(data.min) / this.faktor;

      let max = null;

      if (data.max === this.resolution - 1) {
        max = this.filter.extremes.max;
      } else if (this.filter.extremes.min < 0 && this.filter.extremes.max < 0) {
        // CASE: min: - max: -
        max = this.filter.extremes.min + data.max / this.faktor;
      } else if (this.filter.extremes.min < 0 && this.filter.extremes.max >= 0) {
        // CASE: min: - max: +
        max = data.max / this.faktor - this.filter.extremes.min * -1;
      } else {
        // CASE: min: + max: +
        max = data.max / this.faktor;
      }

      const newExtremes = {
        min: parseFloat(min).toFixed(2),
        max: parseFloat(max).toFixed(2)
      };

      this.$emit('updateHistogramFilter', {
        fid: this.filter.fid,
        id: this.filter.id,
        data: newExtremes
      });
    },
    onSetExtremes() {
      this.chart.chart.xAxis[0].setExtremes(this.getProcessedMin(), this.getProcessedMax());
    },
    minFormatter(value) {
      if (this.filter.type === 'osc') value = value / 100;
      // If a user extreme min has already been set use it, otherwise the min extreme from server
      const fallbackValue = this.filter.userExtremes.min ? this.filter.userExtremes.min : this.filter.extremes.min;

      // Check if the entered value is in range
      const rangeCheckedValue = value < this.filter.extremes.min ? fallbackValue : value;

      // Optional formatting of the value needed in case the current filter is an oscillator
      const filterTypeCheckedValue = this.filter.type === 'osc' ? rangeCheckedValue * 100 : rangeCheckedValue;

      return this.getLocaledValue(filterTypeCheckedValue);
    },
    maxFormatter(value) {
      if (this.filter.type === 'osc') value = value / 100;
      const fallbackValue = this.filter.userExtremes.max ? this.filter.userExtremes.max : this.filter.extremes.max;
      const rangeCheckedValue = value > this.filter.extremes.max ? fallbackValue : value;

      const filterTypeCheckedValue = this.filter.type === 'osc' ? rangeCheckedValue * 100 : rangeCheckedValue;

      return this.getLocaledValue(filterTypeCheckedValue);
    },
    getProcessedMin() {
      return this.filter.userExtremes.min !== null
        ? Math.abs((this.filter.extremes.min - parseFloat(this.filter.userExtremes.min)) * this.faktor)
        : 0;
    },
    getProcessedMax() {
      let max = null;

      if (this.filter.userExtremes.max === null) {
        max = this.resolution;
      } else if (this.filter.userExtremes.min < 0 && this.filter.userExtremes.max < 0) {
        max = this.resolution - (this.filter.userExtremes.max - this.filter.extremes.max) * this.faktor * -1;
      } else if (this.filter.extremes.min < 0 && this.filter.extremes.max >= 0) {
        max = (this.filter.extremes.min - this.filter.userExtremes.max) * this.faktor * -1;
      } else {
        max = this.filter.userExtremes.max * this.faktor;
      }

      return max;
    },
    calculateHistogramFaktor() {
      let extremesRange = null;

      if (this.filter.extremes.min < 0 && this.filter.extremes.max < 0) {
        extremesRange = Math.abs(parseFloat(this.filter.extremes.min)) - Math.abs(parseFloat(this.filter.extremes.max));
      } else if (this.filter.extremes.min <= 0 && this.filter.extremes.max >= 0) {
        extremesRange = Math.abs(parseFloat(this.filter.extremes.min)) + parseFloat(this.filter.extremes.max);
      } else {
        extremesRange = Math.abs(parseFloat(this.filter.extremes.max) - parseFloat(this.filter.extremes.min));
      }

      this.faktor = this.resolution / extremesRange;
    },
    cleanValue(val) {
      if (typeof val === 'number') return val;
      if (!val) return '';
      if (this.decimalSeparator === ',') val = val.replace(/,(?=[^,]+$)/, '.');
      val = val.replace(/[^\d.-]/g, ''); // cleanup number from char debris
      return val ? Number(val) : '';
    },
    getLocaledValue(value) {
      value = this.$f(value, 'float.price');
      return this.decimalSeparator !== '.' ? String(value).replace('.', this.decimalSeparator) : value;
    }
  },
  beforeMount() {
    this.calculateHistogramFaktor();
  },
  mounted() {
    // NOTE: update method is calling afterSetExtremes initially, so no need to call setExtremes/onSetExtremes in mounted-hook
    this.chart.get(this.seriesHistogram.id).update({
      data: this.filter.data
    });
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-histogram-filter';
</style>
